/**
 * Events Service - Fetches events from Mango Business System API
 */

import { CacheManager } from '../utils/cacheManager';
import { API_ENDPOINTS, API_BASE_URL, BUSINESS_ID, buildQueryString } from '../config/api';

class EventsService {
  constructor() {
    this.cacheManager = new CacheManager(5 * 60 * 1000); // 5 minutes cache
  }

  /**
   * Get all events from Mango API
   * @returns {Promise<Array>} Array of event objects
   */
  async getEvents(options = {}) {
    // Check cache first
    if (!options.forceRefresh) {
      const cached = this.cacheManager.get();
      if (cached) {
        return cached;
      }
    }

    try {
      console.log('🌐 Fetching events from Mango API...');
      
      const queryParams = {
        businessId: BUSINESS_ID,
        isActive: 'true',
        limit: options.limit || 50,
        page: options.page || 1,
      };
      
      const queryString = buildQueryString(queryParams);
      const url = `${API_ENDPOINTS.events.public}?${queryString}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const events = data.events || [];
      
      console.log('✅ Events loaded from Mango API:', events.length);
      
      // Cache the results
      this.cacheManager.set(events);
      
      return events;
    } catch (error) {
      console.error('❌ Error fetching events from Mango API:', error);
      return [];
    }
  }

  /**
   * Get a specific event by ID from Mango API
   * @param {string} eventId - The event ID
   * @returns {Promise<Object|null>} Event data or null if not found
   */
  async getEvent(eventId) {
    try {
      console.log(`🔍 Fetching event data for: ${eventId}`);
      
      const url = API_ENDPOINTS.events.publicById(eventId);
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const event = data.event || data;
      
      console.log('✅ Event data loaded:', event);
      return event;
    } catch (error) {
      console.error(`❌ Error fetching event ${eventId}:`, error);
      return null;
    }
  }

  /**
   * Get event photos
   * @param {string} eventId - The event ID
   * @returns {Promise<Array>} Array of photo objects
   */
  async getEventPhotos(eventId) {
    try {
      const url = `${API_ENDPOINTS.events.publicById(eventId)}/photos`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.photos || [];
    } catch (error) {
      console.error(`❌ Error fetching event photos:`, error);
      return [];
    }
  }

  /**
   * Book a ticket for an event
   * @param {string} eventId - The event ID
   * @param {string} customerId - The customer ID
   * @param {number} purchasePrice - The price paid
   * @param {string} paymentMethod - Payment method (stripe, cash, etc.)
   * @param {string} paymentId - Payment ID if applicable
   * @returns {Promise<Object>} Ticket data
   */
  async bookTicket(eventId, customerId, purchasePrice, paymentMethod = 'stripe', paymentId = null) {
    try {
      console.log(`🎫 Booking ticket for event: ${eventId}`);
      
      const url = `${API_BASE_URL}/events/${eventId}/tickets`;
      const token = localStorage.getItem('customerToken'); // Assuming customer auth token
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          customerId,
          purchasePrice,
          paymentMethod,
          paymentId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Ticket booked successfully:', data);
      return data;
    } catch (error) {
      console.error(`❌ Error booking ticket:`, error);
      throw error;
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cacheManager.clear();
    console.log('🗑️ Events cache cleared');
  }
}

// Export a singleton instance
const eventsServiceInstance = new EventsService();
export default eventsServiceInstance;